/* eslint-disable react/no-unescaped-entities */
import PillarPage from "./PillarPage";

const HERO = "https://images.unsplash.com/photo-1518684079-3c830dcef090?w=1600";

const TOC = [
  { id: "what-is-dld", label: "What is the DLD fee?" },
  { id: "breakdown", label: "Every fee, itemised" },
  { id: "worked-example", label: "Worked example — AED 3M apartment" },
  { id: "who-pays", label: "Who pays what (buyer vs seller)" },
  { id: "off-plan", label: "Off-plan vs ready — differences" },
  { id: "reducing", label: "Legal ways to reduce closing costs" },
  { id: "calculator", label: "Point Penta calculator" },
];

const SECTIONS = [
  {
    id: "what-is-dld",
    heading: "What exactly is the DLD fee?",
    body: (
      <>
        <p>
          The Dubai Land Department (DLD) fee is the government transfer tax paid whenever real estate
          changes hands in Dubai. It is <b>4% of the property value</b>, plus a fixed administration fee.
          It is the largest single closing cost after the price itself and is <b>always due at transfer</b>,
          regardless of whether the sale is cash, mortgaged or off-plan.
        </p>
        <p>
          The 4% has been the standard rate since 2013. In practice most buyer-seller deals split the DLD
          fee 50/50 by convention, but the legal responsibility sits with the <b>buyer</b>.
        </p>
      </>
    ),
  },
  {
    id: "breakdown",
    heading: "Complete Dubai closing cost breakdown — 2026",
    body: (
      <table>
        <thead>
          <tr><th>Fee</th><th>Amount</th><th>Paid to</th></tr>
        </thead>
        <tbody>
          <tr><td>DLD transfer fee</td><td>4% of purchase price</td><td>Dubai Land Department</td></tr>
          <tr><td>DLD admin fee</td><td>AED 580</td><td>Dubai Land Department</td></tr>
          <tr><td>Property registration (Oqood — off-plan)</td><td>AED 3,000</td><td>DLD</td></tr>
          <tr><td>Title deed issuance</td><td>AED 250</td><td>DLD</td></tr>
          <tr><td>Broker commission</td><td>2% of price (+5% VAT)</td><td>Real estate broker</td></tr>
          <tr><td>NOC (No-Objection Certificate)</td><td>AED 500 – 5,000</td><td>Developer</td></tr>
          <tr><td>Conveyancing / legal fees</td><td>AED 2,500 – 8,000</td><td>Conveyancer</td></tr>
          <tr><td>Mortgage registration (if applicable)</td><td>0.25% of loan + AED 290</td><td>DLD</td></tr>
          <tr><td>Bank processing fee (if applicable)</td><td>0.5% – 1% of loan</td><td>Lender</td></tr>
          <tr><td>Property valuation (if mortgaged)</td><td>AED 2,500 – 3,500</td><td>Lender-appointed valuer</td></tr>
          <tr><td>Trustee office fee</td><td>AED 4,000 (over AED 500K)</td><td>DLD registration trustee</td></tr>
        </tbody>
      </table>
    ),
  },
  {
    id: "worked-example",
    heading: "Worked example — AED 3,000,000 apartment",
    body: (
      <table>
        <thead><tr><th>Item</th><th>Amount (AED)</th></tr></thead>
        <tbody>
          <tr><td>Property price</td><td>3,000,000</td></tr>
          <tr><td>DLD transfer fee (4%)</td><td>120,000</td></tr>
          <tr><td>DLD admin fee</td><td>580</td></tr>
          <tr><td>Title deed</td><td>250</td></tr>
          <tr><td>Broker commission (2% + 5% VAT)</td><td>63,000</td></tr>
          <tr><td>NOC fee</td><td>1,500 (typical)</td></tr>
          <tr><td>Conveyancing</td><td>4,500 (average)</td></tr>
          <tr><td>Trustee office fee</td><td>4,000</td></tr>
          <tr><td><b>Total closing costs</b></td><td><b>193,830</b></td></tr>
          <tr><td><b>Total outlay</b></td><td><b>3,193,830</b></td></tr>
        </tbody>
      </table>
    ),
  },
  {
    id: "who-pays",
    heading: "Who pays what — buyer vs seller",
    body: (
      <>
        <p>Legal responsibility in Dubai defaults to the buyer for most fees. In practice:</p>
        <ul>
          <li><b>Buyer pays:</b> DLD transfer fee, DLD admin, title deed, buyer's broker commission, conveyancing, mortgage-related fees, trustee office fee.</li>
          <li><b>Seller pays:</b> Their own broker commission (typically 2%), the NOC application to the developer, and any outstanding service charges up to transfer date.</li>
          <li><b>Split by convention (negotiable):</b> The 4% DLD fee is often split 50/50 in a competitive market — a strong buyer's negotiation lever in a slower cycle.</li>
        </ul>
      </>
    ),
  },
  {
    id: "off-plan",
    heading: "Off-plan closing costs — what's different",
    body: (
      <ul>
        <li><b>Oqood registration replaces title deed at closing.</b> AED 3,000 flat fee. Title deed is issued only on handover.</li>
        <li><b>DLD fee applies but is often absorbed by developer</b> during launch promotions. Always confirm in writing.</li>
        <li><b>NOC not required at initial purchase</b> — only for resale before handover.</li>
        <li><b>No broker commission when buying direct from developer</b> — Point Penta earns commission from the developer, not the buyer, for off-plan.</li>
      </ul>
    ),
  },
  {
    id: "reducing",
    heading: "Legal ways to reduce closing costs",
    body: (
      <ol>
        <li><b>Buy during launch waivers.</b> Emaar, DAMAC, Sobha and Meraas periodically absorb the 4% DLD in launch campaigns — sometimes worth AED 100K+.</li>
        <li><b>Negotiate the DLD split.</b> Especially in secondary market, sellers often agree to 50/50.</li>
        <li><b>Buy under a corporate structure</b> if you plan multiple properties — one-time set-up cost, ongoing savings on administrative fees.</li>
        <li><b>Post-handover payment plans</b> reduce the need for bank fees since you finance directly with the developer.</li>
        <li><b>Point Penta bundles conveyancing free of charge</b> above AED 3M — one less line item.</li>
      </ol>
    ),
  },
  {
    id: "calculator",
    heading: "Run your own numbers",
    body: (
      <p>
        Use <a href="/calculators">our Dubai closing cost calculator</a> to model your exact scenario in
        30 seconds. Plug in the price, deposit and loan amount — you'll get a fully itemised breakdown
        matching this article.
      </p>
    ),
  },
];

const FAQS = [
  { q: "What is the DLD fee in Dubai in 2026?", a: "The Dubai Land Department transfer fee is 4% of the property value, plus a small admin fee of AED 580. It has remained at 4% since 2013 and applies to every property transfer regardless of whether the sale is cash, mortgaged or off-plan." },
  { q: "Who pays the DLD fee — buyer or seller?", a: "Legally the buyer is responsible for the 4% DLD fee. In practice, buyers and sellers often split it 50/50 in the secondary market, but this must be negotiated and put in writing." },
  { q: "How much does it cost to buy a property in Dubai in total?", a: "Beyond the price, budget around 7% of the property value for closing costs: 4% DLD, 2% broker commission (+VAT), and about 1% in miscellaneous fees (NOC, registration, oqood, title deed, trustee office, conveyancing)." },
  { q: "Is the DLD fee refundable?", a: "No. Once paid to the Dubai Land Department at transfer, the 4% fee is non-refundable, even if the sale is later reversed." },
  { q: "Do off-plan buyers pay the DLD fee?", a: "Yes, off-plan buyers pay the 4% DLD fee at the point of Oqood registration (or at handover, depending on developer). Many developers absorb this fee during launch promotions — always check the SPA." },
  { q: "Can I negotiate the DLD fee down?", a: "The 4% itself is fixed by government. What is negotiable is who pays it — sellers regularly agree to split it 50/50 with buyers, and developers often absorb it entirely during off-plan launches." },
];

export default function DLDFeesPillar() {
  return (
    <PillarPage
      slug="dubai-property-closing-costs"
      category="Fees & taxation"
      title="Dubai Property Closing Costs — DLD Fees Explained (2026)"
      hero={HERO}
      updated="Feb 2026"
      intro="The Dubai Land Department (DLD) transfer fee is 4% of the property value — the single biggest cost after the price itself. This guide gives you every fee, exact 2026 rates, a full worked example on an AED 3M purchase and Point Penta's playbook for legally reducing them."
      toc={TOC}
      sections={SECTIONS}
      faqs={FAQS}
    />
  );
}
